import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, FormControl, Validators, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { LoadingService } from '@app/_services/LoadingService/loading.service';
import { PageDto } from '@app/shared/model/page.model';
import { RoleDto } from '@app/shared/model/role.model';
import { CLAIMS, ValidationConstants } from '@app/shared/utilities/constants';
import { decrypt } from '@app/utilities/enc-dec.util';
import { UtilityService } from '@app/utilities/utility.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-role',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule,NgbModule],
  templateUrl: './add-role.component.html',
  styleUrl: './add-role.component.scss'
})
export class AddRoleComponent {
  openedAccordions: boolean[] = [];
  public readonly CLAIMS = CLAIMS;
  public readonly ValidationConstants=ValidationConstants;
  pages!:PageDto[];
  roleForm!: FormGroup;
  role!:RoleDto;
  selectedRole!: RoleDto | null;
  roleId!:string|null;
  isEdit=false;
  isLoaded=false; // To track the opened accordion items

  constructor(
    private fb: FormBuilder,
    private toastService: ToastrService,
    private apiRequestService: ApiRequestService,
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoadingService,
    private utility: UtilityService


  ) {}
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
        // this.roleId = params.get('id'); // Retrieve 'id' from the route
        const encryptedId = params.get('id');
        if (encryptedId && encryptedId !== 'new') {
           const decryptedId = decrypt(encryptedId);
           this.roleId = decryptedId;
        }
        if (this.roleId) {
          this.isEdit=true
            this.getRole(); // If roleId exists, get the role for editing
        } else {
            this.getPages(); // If no roleId, load pages for adding a new role
        }
    });
}

initForm(): void {
  const permissionsControls: { [key: string]: FormControl } = {}; 
  this.pages.forEach(role => {
    role.actions.forEach(action => {
      const controlName = this.getPermissionControlName(action.code, action.id);
      permissionsControls[controlName] = new FormControl(false); // Initialize all controls to false
    });
  });
  this.roleForm = this.fb.group({
    roleName: ['', [Validators.required,Validators.maxLength(ValidationConstants.MAX_NAME_LENGTH)]],
    ...permissionsControls
  });
}
  getRole(): void {
    this.apiRequestService.getRequest<RoleDto>(`Role/${this.roleId}`,null,false)
      .subscribe((response: HttpResponse<RoleDto>) => {
        if (response.body) {
          this.selectedRole = response.body;
          this.getPages(); // Fetch pages to get actions and set form controls
        }
      }, error => {
        this.toastService.error('Failed to load roles!', 'Error');
      });
}
toggleAccordion(index: number): void {
  this.openedAccordions[index] = !this.openedAccordions[index];
}
getPages(): void {
  this.apiRequestService.getRequest<PageDto[]>(`Page`,null, false)  // Prevent auto-hide of loader
    .subscribe((response: HttpResponse<PageDto[]>) => {
      if (response.body) {
        this.pages = response.body;
        this.initForm(); // Initialize the form with pages and actions
        this.pages.forEach((page, index) => {
          this.openedAccordions[index] = true;
        });

        if (this.selectedRole) {
          const permissionControls = this.setPermissionsForEdit(this.selectedRole.roleClaims);
          this.patchPermissions(permissionControls, this.selectedRole.name);              
        }
          this.isLoaded = true;  
          this.loaderService.hideLoader(); 
        
      }
    }, error => {
      this.toastService.error('Failed to load pages!', 'Error');
      this.isLoaded = true;  
      this.loaderService.hideLoader();  // Ensure loader is hidden on error as well
    });
}

  getPermissionControlName(claimType: string, actionId: number): string {
    return `role_${claimType}_action_${actionId}`;
  }
  createRolePayload(): any {
    const selectedPermissions = this.extractSelectedPermissions();   
    return {
      id: this.roleForm.value.id || 0,  
      name: this.roleForm.value.roleName,  
      isActive: this.roleForm.value.isActive || true,  
      roleClaims: selectedPermissions.map(permission => ({
        roleId: this.roleForm.value.id || 0, 
        claimType: permission.code, 
        claimValue: '',  
        actionId: permission.id 
      }))
    };
  }
  
  extractSelectedPermissions(): any[] {
    const selectedPermissions: any[] = [];
    this.pages.forEach(role => {
      role.actions.forEach(action => {
        const controlName = this.getPermissionControlName(action.code, action.id);
        if (this.roleForm?.get(controlName)?.value) {
          selectedPermissions.push({
            roleId: role.id,
           ...action 
          });
        }
      });
    });
    return selectedPermissions;
  }
  patchPermissions(permissions: { [key: string]: boolean },name:string): void {
    this.roleForm.get('roleName')?.patchValue(name)
    Object.keys(permissions).forEach(controlName => {
      if (this.roleForm.contains(controlName)) {
        this.roleForm.get(controlName)?.patchValue(permissions[controlName]); // Patch the control value
      }
    });
  }
  
  setPermissionsForEdit(roleClaims: any[]): { [key: string]: boolean } {   
    const permissionControls: any = {};
    roleClaims.forEach(claim => {
      const controlName = this.getPermissionControlName(claim.claimType, claim.actionId);
      permissionControls[controlName] = true; // Set the permission to true based on the claim
    });
    return permissionControls;
}
  onSubmit(): void {
    
    this.utility.markFormGroupTouched(this.roleForm); 
    const invalidRoleFields = this.getInvalidControls(this.roleForm);

    if (this.roleForm.valid) {
      const role: any = this.createRolePayload(); 
      if (this.selectedRole) {
        this.selectedRole.name = role.name;
        this.selectedRole.isActive = role.isActive;
        this.selectedRole.roleClaims = role.roleClaims.map((claim: any, index: number) => ({
          ...claim,
          roleId: this.selectedRole?.id
        }));
        
        const payload = this.selectedRole; 
        this.apiRequestService.updateRequest<any>(`Role`, payload)
          .subscribe((response: HttpResponse<any>) => {
            if (response.status === 200 && response.body) {
              this.toastService.success('Role updated successfully!', 'Success');
              this.router.navigate([`role`]);
            } else {
              this.toastService.error('Some error occurred!', 'Error');
            }
          }, err => {
            const error = err.error || 'Some error occurred!';
            this.toastService.error(error, 'Error');
          });
      } else {
        const payload = role;  
        this.apiRequestService.postRequest<any>('Role', payload)
          .subscribe((response: HttpResponse<any>) => {
            if (response.status === 200 && response.body) {
              this.toastService.success('Role added successfully!', 'Success');
              this.router.navigate([`role`]);
            } else {
              this.toastService.error('Some error occurred!', 'Error');
            }
          }, err => {
            const error = err.error || 'Some error occurred!';
            this.toastService.error(error, 'Error');
          });
      }
  
    } else if (invalidRoleFields.length) {
      const firstInvalidField = this.getFieldName(invalidRoleFields[0]);
      this.toastService.error(` ${firstInvalidField}`, 'Error');
    }
  }

toggleSelectAll(event: Event): void {
  const isChecked = (event.target as HTMLInputElement).checked;
  Object.keys(this.roleForm.controls).forEach(controlName => {
      if (controlName !== 'roleName') { // Exclude 'roleName'
          this.roleForm.controls[controlName].setValue(isChecked);
      }
  });
}

  resetForm(): void {
    this.roleForm.reset();
    this.selectedRole = null;
    const selectAllCheckbox = document.getElementById('selectAllPermissions') as HTMLInputElement;
            if (selectAllCheckbox) {
                selectAllCheckbox.checked = false; // Uncheck the "Select All" checkbox
            }
  }

  
  getInvalidControls(formGroup: FormGroup): string[] {
    const invalidControls: string[] = [];
  
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
  
      if (control && control.invalid && control.touched) {
        const controlErrors = this.getControlErrors(key, control.errors);
        if (controlErrors) {
          invalidControls.push(controlErrors);
        }
      }
  
      // Check for nested form groups
      if (control instanceof FormGroup) {
        invalidControls.push(...this.getInvalidControls(control));
      }
    });
    return invalidControls;
  }
  getControlErrors(controlName: string, errors: ValidationErrors | null): string | null {
    if (!errors) return null;
    const fieldNames: { [key: string]: string } = {
      roleName: 'Role Name',
    };
    
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                             
    const fieldName = fieldNames[controlName] || controlName; // Get friendly field name
    let errorMessage = '';
  
    // Customize messages based on the error type
    if (errors['required']) {
      errorMessage = `${fieldName} is required.`;
    } else if (errors['maxlength']) {
      errorMessage = `${fieldName} exceeds the maximum length.`;
    } else if (errors['pattern']) {
      errorMessage = `${fieldName} has an invalid format.`;
    } else if (errors['invalidEmail']) {
      errorMessage = `${fieldName} is not a valid email address.`;
    }
  
    return errorMessage;
  }
  private getFieldName(fieldKey: string): string {
    const fieldNamesMap: { [key: string]: string } = {
      'roleName': 'Role Name',
    };
    return fieldNamesMap[fieldKey] || fieldKey;  // Fallback to the fieldKey if not mapped
  }
}